(() => {
  if (customElements.get('testimonial-video')) return;

  const coordinators = new WeakMap();
  const isEligibleVideo = (video) => Boolean(
    video?.querySelector('video')
    && video.closest('.testimonial-video-card')?.querySelector('[data-testimonial-video-toggle]')
  );
  const playVideo = (video) => {
    if (typeof video?.play === 'function') video.play();
  };

  class TestimonialVideoCoordinator {
    constructor(section) {
      this.section = section;
      this.videos = new Set();
      this.carouselObservers = new Map();
      this.initializedCarousels = new WeakSet();
      this.gridInitialized = false;
    }

    register(video) {
      this.videos.add(video);
      this.initializeAutoplay(video);
    }

    unregister(video) {
      this.videos.delete(video);

      if (!this.videos.size) {
        this.carouselObservers.forEach((observer) => observer.disconnect());
        this.carouselObservers.clear();
        this.initializedCarousels = new WeakSet();
        this.gridInitialized = false;
      }
    }

    pauseSiblings(video) {
      this.videos.forEach((sibling) => {
        if (sibling !== video) sibling.video?.pause();
      });
    }

    muteSiblings(video) {
      this.videos.forEach((sibling) => {
        if (sibling !== video) sibling.mute();
      });
    }

    initializeAutoplay(video) {
      const carousel = video.closest('component-carousel');

      if (carousel) {
        this.initializeCarouselAutoplay(carousel);
      } else if (!this.gridInitialized && this.firstEligibleVideo() === video) {
        this.gridInitialized = true;
        playVideo(video);
      }
    }

    initializeCarouselAutoplay(carousel) {
      if (this.initializedCarousels.has(carousel)) return;

      const slider = carousel.querySelector('.keen-slider');
      const slides = slider?.querySelectorAll('.keen-slider-slide');
      if (!slider || !slides?.length) return;

      if ([...slides].some((slide) => !slide.hasAttribute('aria-hidden'))) {
        if (this.carouselObservers.has(carousel)) return;

        const observer = new MutationObserver(() => this.initializeCarouselAutoplay(carousel));
        this.carouselObservers.set(carousel, observer);
        observer.observe(slider, { attributes: true, attributeFilter: ['aria-hidden'], subtree: true });
        return;
      }

      const initialVideo = [...slides]
        .filter((slide) => slide.getAttribute('aria-hidden') === 'false')
        .map((slide) => slide.querySelector('testimonial-video'))
        .find(isEligibleVideo);

      if (!initialVideo) {
        this.initializedCarousels.add(carousel);
      } else if (typeof initialVideo.play === 'function') {
        this.initializedCarousels.add(carousel);
        playVideo(initialVideo);
      } else {
        return;
      }

      this.carouselObservers.get(carousel)?.disconnect();
      this.carouselObservers.delete(carousel);
    }

    firstEligibleVideo() {
      return [...this.section.querySelectorAll('testimonial-video')]
        .find(isEligibleVideo);
    }
  }

  const getCoordinator = (section) => {
    if (!coordinators.has(section)) {
      coordinators.set(section, new TestimonialVideoCoordinator(section));
    }

    return coordinators.get(section);
  };

  class TestimonialVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.video = this.querySelector('video');
      this.card = this.closest('.testimonial-video-card');
      this.button = this.card?.querySelector('[data-testimonial-video-toggle]');
      this.playIcon = this.button?.querySelector('[data-testimonial-video-icon="play"]');
      this.pauseIcon = this.button?.querySelector('[data-testimonial-video-icon="pause"]');
      this.muteButton = this.card?.querySelector('[data-testimonial-video-mute]');
      this.muteIcon = this.muteButton?.querySelector('[data-testimonial-video-mute-icon="mute"]');
      this.unmuteIcon = this.muteButton?.querySelector('[data-testimonial-video-mute-icon="unmute"]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.section = this.closest('.type_Testimonials');
      this.coordinator = this.section ? getCoordinator(this.section) : null;
      this.handleToggle = this.toggle.bind(this);
      this.handleMuteToggle = this.toggleMute.bind(this);
      this.handleStateChange = this.updateControl.bind(this);
      this.handleReducedMotionChange = this.handleReducedMotion.bind(this);

      this.button?.addEventListener('click', this.handleToggle);
      this.muteButton?.addEventListener('click', this.handleMuteToggle);
      this.video?.addEventListener('play', this.handleStateChange);
      this.video?.addEventListener('pause', this.handleStateChange);
      this.video?.addEventListener('volumechange', this.handleStateChange);
      this.reducedMotion.addEventListener('change', this.handleReducedMotionChange);

      this.updateControl();
      this.updateMuteControl();
      this.coordinator?.register(this);
    }

    disconnectedCallback() {
      try {
        this.video?.pause();
      } catch {
      }
      this.updateControl();
      this.updateMuteControl();
      this.button?.removeEventListener('click', this.handleToggle);
      this.muteButton?.removeEventListener('click', this.handleMuteToggle);
      this.video?.removeEventListener('play', this.handleStateChange);
      this.video?.removeEventListener('pause', this.handleStateChange);
      this.video?.removeEventListener('volumechange', this.handleStateChange);
      this.reducedMotion?.removeEventListener('change', this.handleReducedMotionChange);
      this.coordinator?.unregister(this);
      this.coordinator = null;
      this.initialized = false;
    }

    toggle() {
      if (this.video?.paused) {
        this.play(true);
      } else {
        this.video.pause();
      }
    }

    toggleMute() {
      if (!this.video) return;

      if (this.video.muted) {
        this.unmute();
      } else {
        this.mute();
      }
    }

    mute() {
      if (!this.video) return;
      this.video.muted = true;
      this.updateMuteControl();
    }

    unmute() {
      if (!this.video) return;
      this.coordinator?.muteSiblings(this);
      this.video.muted = false;
      this.updateMuteControl();
    }

    play(allowReducedMotion = false) {
      if (!this.video || (this.reducedMotion.matches && !allowReducedMotion)) return;

      this.coordinator?.pauseSiblings(this);
      try {
        const playPromise = this.video.play();
        if (playPromise) playPromise.catch(() => {
          this.video.pause();
          this.updateControl();
        });
      } catch {
        this.updateControl();
      }
    }

    handleReducedMotion(event) {
      if (event.matches) this.video?.pause();
    }

    updateControl() {
      if (!this.button || !this.video) return;

      const isPlaying = !this.video.paused;
      this.button.setAttribute('aria-label', isPlaying ? this.dataset.pauseLabel : this.dataset.playLabel);
      this.button.setAttribute('aria-pressed', String(isPlaying));
      this.playIcon.hidden = isPlaying;
      this.pauseIcon.hidden = !isPlaying;
    }

    updateMuteControl() {
      if (!this.muteButton || !this.video) return;

      const isMuted = this.video.muted;
      this.muteButton.setAttribute('aria-label', isMuted ? this.dataset.unmuteLabel : this.dataset.muteLabel);
      this.muteButton.setAttribute('aria-pressed', String(isMuted));
      if (this.muteIcon) this.muteIcon.hidden = !isMuted;
      if (this.unmuteIcon) this.unmuteIcon.hidden = isMuted;
    }
  }

  customElements.define('testimonial-video', TestimonialVideo);
})();
